import React from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactFlow, { Controls, Edge, Node, useEdgesState, useNodesState, Background, ReactFlowInstance, addEdge, MiniMap, BackgroundVariant } from "reactflow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMap } from "@fortawesome/free-regular-svg-icons";
import axios from "axios";
import { workflowItems } from "../../pages/workflow";
import { CustomNode } from "../nodes";
import { CustomEdge } from "../edges";
import { RunActions } from "../run";
import { RootState } from "../../redux/store";
import { consoleURL, webURL } from "../../services/API";
import { handleWorkflowSave } from "../../services/method";
import { Main } from "./style";
// @ts-ignore
import 'reactflow/dist/style.css';
import { ProjectLog } from "../../pages/projects/log";
import { MonitorLog } from "./monitorLog";
import { useDispatch } from 'react-redux';
import { setProjectData } from '../../redux/reducers/project'
import { LiveLog } from "./liveLog";
import socket from "../../services/socket";
// import { ChatSidebar } from "../chatSidebar";
// import { faRobot } from "@fortawesome/free-solid-svg-icons";

const nodeTypes = { customNode: CustomNode };
const edgeTypes = { customEdge: CustomEdge };
let timeoutId: any = 0;

export function View() {
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const showContactUS = useSelector((state: RootState) => state.settings.data.showContactUS);
   const isThereBlockExpanded = useSelector((state: RootState) => state.project.isThereBlockExpanded);
   const [searchParams] = useSearchParams();
   const projectId = searchParams.get('id');
   const [toggleMap, setToggleMap] = React.useState<boolean>(false);
   const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>([]);
   const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>([]);
   const [project, setProject] = React.useState<projectType | null>(null);
   const [reactFlowInstance, setReactFlowInstance] = React.useState<ReactFlowInstance | null>(null);
   const dispatch = useDispatch();
   // Chat sidebar state
   // const [isChatCollapsed, setIsChatCollapsed] = React.useState<boolean>(true);
   // const [chatWidth, setChatWidth] = React.useState<number>(400);
   // ---- Undo / Redo
   const [historyNodes, setHistoryNodes] = React.useState<Node[][]>([]);
   const [, setFutureNodes] = React.useState<Node[][]>([]);

   React.useEffect(() => {
      const lastHistoryLength = historyNodes[historyNodes.length - 1]?.length || 0;

      if (nodes.length !== lastHistoryLength) {
         setHistoryNodes(history => {
            const updated = [...history, nodes];
            return updated.slice(-5);
         });
         setFutureNodes([]);
      }
   // eslint-disable-next-line
   }, [nodes]);

   React.useEffect(() => {
      const handleKeys = (e: KeyboardEvent) => {
         if (isThereBlockExpanded) return;
         // ---- Ctrl + Z (Undo)
         if (e.ctrlKey && e.code === 'KeyZ') {
            setHistoryNodes(prevHistory => {
               if (prevHistory.length <= 1) return prevHistory;
               const newHistory = [...prevHistory];
               const current = newHistory.pop();
               const previous = newHistory[newHistory.length - 1];

               if (previous) {
                  if (current) setFutureNodes(f => [...f, current]);
                  setNodes(previous);
               }
               return newHistory;
            });
         }
         // ---- Ctrl + Y (Redo)
         if (e.ctrlKey && e.code === 'KeyY') {
            setFutureNodes(prevFuture => {
               if (!prevFuture.length) return prevFuture;
               const newFuture = [...prevFuture];
               const nextState = newFuture.pop();

               if (nextState) {
                  setNodes(nextState);
                  setHistoryNodes(h => [...h, nextState].slice(-5));
               }
               return newFuture;
            });
         }
      }
      window.addEventListener('keydown', handleKeys);
      return () => window.removeEventListener('keydown', handleKeys);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isThereBlockExpanded]);

   // --- Function to fetch project data
   const fetchProjectData = React.useCallback(async () => {
      if (userToken && projectId) {
         try {
            const response = await axios.get(`${webURL}/projects/workflow?id=${projectId}`, { headers: { 'x-auth-token': userToken } });
            for (const node of response.data.nodes) {
               const action = workflowItems.find(item => item.action === node.data.action);
               if (action) {
                  node.data.executeRun = action.executeRun;
                  node.data.background = action.background;
                  node.data.icon = action.icon;
                  if (action.variablesRef) node.data.variablesRef = action.variablesRef;
                  else delete node.data.variablesRef;
                  node.data.postions = action.postions;
               }
            }
            setNodes(response.data.nodes);
            setHistoryNodes(history => [...history, response.data.nodes]);
            setEdges(response.data.edges.map((e: any) => ({ ...e, animated: true })));
            setProject({ _id: projectId, ...response.data });
            dispatch(setProjectData({
               id: projectId, name: response.data.name, isLoop: !!response.data.loop?.isStarting,
               getExportAPIURL: `${webURL}/run/${projectId}`, getExportAPIKey: response.data.apiKey,
               getLastRunDate: response.data.lastRun, getDateOfCreation: response.data.registration
            }));
         } catch (err) {
            window.location.href = consoleURL;
            console.log(err);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userToken, projectId]);

   // ---   
   React.useEffect(() => {
      if (userToken) {
         if (!projectId) window.location.href = consoleURL;
         else fetchProjectData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userToken]);

   // 
   React.useEffect(() => {
      const handleReFetchData = () => { if (!isWorkflowRun) fetchProjectData() };
      socket.off('reFetchData_workflowBuilder');
      socket.on('reFetchData_workflowBuilder', handleReFetchData);
      return () => { socket.off('reFetchData_workflowBuilder', handleReFetchData) }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fetchProjectData, isWorkflowRun]);

   // --- Listen for workflow refresh events from chat sidebar
   React.useEffect(() => {
      const handleRefreshWorkflow = () => { fetchProjectData() };
      window.addEventListener('refreshWorkflow', handleRefreshWorkflow);
      return () => window.removeEventListener('refreshWorkflow', handleRefreshWorkflow);
   }, [fetchProjectData]);

   // ---
   React.useEffect(() => {
      clearTimeout(timeoutId);
      if (nodes.length) {
         timeoutId = setTimeout(() => {
            if (userToken && !isWorkflowRun) {
               handleWorkflowSave(searchParams.get('id') as string, userToken, nodes, edges);
            }
         }, 5000);
      }
      return () => clearTimeout(timeoutId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userToken, nodes, edges]);

   // ---
   const onDragOver = React.useCallback((event: React.DragEvent<HTMLLIElement>) => {
      event.preventDefault(); event.dataTransfer.dropEffect = "move";
   }, []);

   // ---
   const onDrop = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const action = event.dataTransfer.getData("application/reactflow");
      const currentCategory = event.dataTransfer.getData("text/plain");

      if (!reactFlowInstance || typeof action === "undefined" || !action) return;
      const position = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });

      setNodes((nds) => {
         // Prevent multiple start nodes
         if (action === 'start' && nds.some((node: any) => node.data.action === 'start')) {
            alert('Only one Start node is allowed per workflow');
            return nds; // Return unchanged nodes
         }
         const filter = nds.filter((i: any) => i.data.action === action).map((o: any) => +o.data.index.match(/\d+/)[0]);
         const index = filter.length ? Math.max(...filter) + 1 : 1;
         return (
            nds.concat({
               id: (nds[nds.length - 1]?.id ? parseInt(nds[nds.length - 1].id) + 1 : 1).toString(), type: 'customNode', position,
               data: { index: action + '-' + (index), ...workflowItems.find(item => item.action === action), currentCategory }
            } as any)
         )
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [reactFlowInstance]);

   // --
   const onConnect = React.useCallback((params: any) => { // @ts-ignore
      const findTarget = nodes.find(node => node.id === params.target); // @ts-ignore
      const isHide = findTarget?.data.postions.find(postion => postion.label === params.targetHandle)?.isHide
      if (isHide) return;

      params.type = 'customEdge'; // @ts-ignore
      params.animated = true;
      // @ts-ignore
      params.data = { color: findTarget?.data?.background };
      setEdges((eds) => {
         // Prevent multiple edges from start node
         const sourceNode = nodes.find(node => node.id === params.source) as any;
         if (sourceNode?.data.action === 'start') {
            const existingOutgoingEdge = eds.find(edge => edge.source === params.source);
            if (existingOutgoingEdge) {
               alert('Start node can only have one outgoing connection');
               return eds; // Return unchanged edges
            }
         }
         return addEdge(params, eds);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [nodes/* edges, setEdges, setNodes */]);

   // ---
   const handleEdgeHover = (now: Edge, value: boolean) => {
      setEdges((edges) =>
         edges.map((edge) => {
            if (edge.id === now.id) return { ...edge, data: { ...now.data, isHover: value } };
            else return edge;
         })
      );
   }

   return (
      <Main /* style={{ paddingRight: isChatCollapsed ? '0px' : `${chatWidth}px` }} */>
         <ReactFlow
            proOptions={{ hideAttribution: true }}
            nodes={nodes}
            nodeTypes={nodeTypes}
            edges={edges}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver as any}
            onEdgeMouseEnter={(_, now) => handleEdgeHover(now, true)}
            onEdgeMouseLeave={(_, now) => handleEdgeHover(now, false)}
            fitView
            style={{ backgroundImage: 'linear-gradient(to right, #f8fafc, #eff6ff)' }}
         >
            <Background
               variant={BackgroundVariant.Dots}
               gap={20}
               size={1}
               color="#e2e8f0"
               className="opacity-30 dark:opacity-20"
            />
            <RunActions project={project} setProject={setProject} />
            <Controls>
               <div className="react-flow__controls-button minimap" title="toggle minimap" onClick={_ => setToggleMap(t => !t)}>
                  <FontAwesomeIcon icon={faMap} />
               </div>
            </Controls>
            {toggleMap ? <MiniMap nodeStrokeWidth={3} /> : null}
            <div className="logs">
               <LiveLog />
               <MonitorLog projectId={userToken ? projectId as string : ''} />
               <ProjectLog
                  projectId={userToken ? projectId as string : ''} // @ts-ignore
                  nodes={nodes}
               />
            </div>
            {/* Chat Icon in top right corner */}
            {/* <div
               className="chat-toggle-icon"
               onClick={() => setIsChatCollapsed(!isChatCollapsed)}
               title="Chat with AI"
            >
               <FontAwesomeIcon icon={faRobot} />
               <span>Chat with Workflow</span>
            </div>
            <ChatSidebar
               isCollapsed={isChatCollapsed}
               onToggle={() => setIsChatCollapsed(!isChatCollapsed)}
               width={chatWidth}
               onWidthChange={setChatWidth}
            /> */}
            <div className={`contactUs ${ showContactUS ? 'show' : ''}`} onClick={ _=> window.open('https://calendly.com/cloudilic', '_blank') }>
               Having an issue? <span>Contact us</span>
            </div>
         </ReactFlow>
      </Main>
   );
}  